import { useState } from 'react';
import {
  mapGetAllReportConfigurationsResponseType,
  mapPersonType,
  ReportConfigurationType,
  useFindPersonDetailsSuspense,
  useGetAllReportConfigurationsSuspense,
} from '@api';
import { ReportMailingForm, ReportMailingsTable } from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Divider, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/persons/$id.reports';
import {
  getAddReportMailingFormSchema,
  getEditReportMailingFormSchema,
  getRecipientOptions,
  mapFormValuesToUpdateRequest,
  mapReportMailingToFormValues,
} from './helpers';
import {
  useAddReportMailing,
  useDeleteReportMailing,
  useUpdateReportMailing,
} from './hooks';
import type {
  AddReportMailingFormValuesType,
  EditReportMailingFormValuesType,
} from './types';

export const PersonReportMailings: React.FC = () => {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { hasPermission } = usePermissionContext();
  const [openedAdd, { open: openModalAdd, close: closeModalAdd }] =
    useDisclosure(false);
  const [selectedMailing, setSelectedMailing] =
    useState<ReportConfigurationType | null>(null);
  const { data: mailings } = useGetAllReportConfigurationsSuspense(
    { personId: id },
    {
      query: {
        select: (response) =>
          mapGetAllReportConfigurationsResponseType(response).results,
      },
    }
  );
  const { data: companies } = useFindPersonDetailsSuspense(id, {
    query: { select: (data) => mapPersonType(data.data).companies },
  });
  const { addReportMailing } = useAddReportMailing();
  const { updateReportMailing } = useUpdateReportMailing();
  const { handleDeleteReportMailing } = useDeleteReportMailing();

  const canWrite = hasPermission({ permission: 'REPORTS', action: 'WRITE' });

  const closeModalEdit = () => setSelectedMailing(null);

  const handleAddReportMailing = async ({
    label,
  }: AddReportMailingFormValuesType) => {
    if (
      await addReportMailing({
        label,
        configurationOptions: { kind: 'PERSON', personId: id },
      })
    ) {
      closeModalAdd();
    }
  };

  const handleUpdateReportMailing = async (
    values: EditReportMailingFormValuesType
  ) => {
    if (!selectedMailing) return;

    if (
      await updateReportMailing(
        selectedMailing.id,
        mapFormValuesToUpdateRequest(values)
      )
    ) {
      closeModalEdit();
    }
  };

  return (
    <>
      <ReportMailingsTable
        variant="PERSON"
        mailings={mailings}
        canAdd={canWrite}
        onAdd={openModalAdd}
        canEdit={canWrite}
        onEdit={setSelectedMailing}
        onDelete={handleDeleteReportMailing}
      />
      <Modal
        opened={openedAdd}
        onClose={closeModalAdd}
        title={t('REPORTS.MAILING_FORM.ADD_TITLE')}
      >
        <ReportMailingForm.Root<AddReportMailingFormValuesType>
          schema={getAddReportMailingFormSchema(t)}
          defaultValues={{ label: '' }}
          onSave={handleAddReportMailing}
          onCancel={closeModalAdd}
        >
          <ReportMailingForm.Label<AddReportMailingFormValuesType> fieldName="label" />
        </ReportMailingForm.Root>
      </Modal>
      <Modal
        opened={selectedMailing !== null}
        onClose={closeModalEdit}
        title={t('REPORTS.MAILING_FORM.EDIT_TITLE')}
        size="lg"
      >
        {selectedMailing && (
          <ReportMailingForm.Root<EditReportMailingFormValuesType>
            schema={getEditReportMailingFormSchema(t)}
            defaultValues={mapReportMailingToFormValues(selectedMailing)}
            onSave={handleUpdateReportMailing}
            onCancel={closeModalEdit}
          >
            <ReportMailingForm.SettingsTitle />
            <ReportMailingForm.Label<EditReportMailingFormValuesType> fieldName="label" />
            <ReportMailingForm.Active<EditReportMailingFormValuesType> fieldName="isEnabled" />
            <ReportMailingForm.Recipient<EditReportMailingFormValuesType>
              fieldName="recipientMail"
              data={getRecipientOptions(companies)}
            />
            <ReportMailingForm.Scope<EditReportMailingFormValuesType> fieldName="scope" />
            <ReportMailingForm.SendDay<EditReportMailingFormValuesType> fieldName="sendWeekday" />
            <ReportMailingForm.SendTime<EditReportMailingFormValuesType>
              hourFieldName="sendHour"
              minuteFieldName="sendMinute"
            />
            <ReportMailingForm.Language<EditReportMailingFormValuesType> fieldName="locale" />
            <ReportMailingForm.Revenue<EditReportMailingFormValuesType> fieldName="includeRevenue" />
            <Divider />
            <ReportMailingForm.EmailNote<EditReportMailingFormValuesType> fieldName="profileNote" />
            <Divider />
            <ReportMailingForm.InternalNote<EditReportMailingFormValuesType> fieldName="internalNote" />
          </ReportMailingForm.Root>
        )}
      </Modal>
    </>
  );
};
